import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUIState } from '../contexts/UIStateContext';
import Sidebar from '../components/Sidebar';
import KnowledgeSidebar from '../components/KnowledgeSidebar';
import FloatingActionMenu from '../components/shared/FloatingActionMenu';
import GraphView from '../components/knowledge/GraphView';
import CreateItemModal from '../components/knowledge/CreateItemModal';
import CreateProductModal from '../components/knowledge/CreateProductModal';
import ItemDetailsModal from '../components/knowledge/ItemDetailsModal';
import { knowledgeApi } from '../lib/knowledge-api';
import { useKnowledgeWebSocket } from '../hooks/useKnowledgeWebSocket';
import { useCollectionRefresh } from '../hooks/useCollectionRefresh';
import { useAIHealth } from '../hooks/useAIHealth';
import toast from 'react-hot-toast';

export default function Knowledge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getKnowledgeState, setKnowledgeSelectedCollections, setKnowledgeLockedCollection, setKnowledgeSearchTerm, setKnowledgeSelectedProduct, setKnowledgeSelectedCategory, setKnowledgeContextFilters, setKnowledgeSharedSlotPositions, getKnowledgeImportProgress, setKnowledgeImportProgress } = useUIState();

  // Initialize state from UIState on mount
  const persistedState = getKnowledgeState();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => window.innerWidth <= 980);
  const [selectedCollections, setSelectedCollections] = useState(persistedState.selectedCollections);
  const [lockedCollectionId, setLockedCollectionId] = useState(persistedState.lockedCollectionId);
  const [searchTerm, setSearchTerm] = useState(persistedState.searchTerm);
  const [selectedProduct, setSelectedProduct] = useState(persistedState.selectedProduct);
  const [selectedCategory, setSelectedCategory] = useState(persistedState.selectedCategory);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeCollectionId, setActiveCollectionId] = useState(null);
  const [contextFilters, setContextFilters] = useState(persistedState.contextFilters);
  const [selectedItem, setSelectedItem] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loadedCollections, setLoadedCollections] = useState({}); // Stores fully loaded collections with slots
  const [sharedSlotPositions, setSharedSlotPositions] = useState(persistedState.sharedSlotPositions);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);
  const [productModalContext, setProductModalContext] = useState(null);
  const [preselectedCollections, setPreselectedCollections] = useState([]);
  const [modalItem, setModalItem] = useState(null);

  // Collection refresh utilities
  const { refreshCollection, refreshCollections, refreshCollectionsWithItem } = useCollectionRefresh(setLoadedCollections);

  /**
   * AI Health check hook - Checks for AI connection issues and creates notifications.
   * This runs on mount to proactively notify users of configuration problems.
   */
  useAIHealth({ checkOnMount: true });

  // Fetch collections function - defined early so it can be used in callbacks
  const fetchCollections = useCallback(async (search = '') => {
    try {
      let collectionsData;
      // Ensure search is a string (defensive coding)
      const searchTerm = typeof search === 'string' ? search : '';
      if (searchTerm && searchTerm.trim()) {
        // Use advanced search endpoint when there's a search term
        collectionsData = await knowledgeApi.collections.search(searchTerm);
      } else {
        // Get all collections when no search
        collectionsData = await knowledgeApi.collections.getAll();
      }
      setCollections(collectionsData);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    }
  }, []);

  // WebSocket handlers for real-time updates
  const handleItemUpdated = useCallback((itemId, itemData) => {
    console.log('[Knowledge] Item updated via WebSocket:', itemId, itemData);

    // Update loadedCollections if any loaded collection contains this item
    setLoadedCollections(prev => {
      const updated = { ...prev };
      let hasUpdate = false;

      Object.keys(updated).forEach(collectionId => {
        const collection = updated[collectionId];
        if (collection?.slots) {
          const updatedSlots = collection.slots.map(slot => {
            if (slot.item?.id === itemId) {
              hasUpdate = true;
              return {
                ...slot,
                item: {
                  ...slot.item,
                  title: itemData.title,
                  tags: itemData.tags
                }
              };
            }
            return slot;
          });

          if (hasUpdate) {
            updated[collectionId] = {
              ...collection,
              slots: updatedSlots
            };
          }
        }
      });

      return hasUpdate ? updated : prev;
    });

    // Also refresh the collections list to ensure any newly created collections are visible
    // This is important when an item creates its own collection
    fetchCollections();

    // Show toast notification based on whether there's an error
    if (itemData.error) {
      // Show error notification if title generation failed
      toast.error(`Title generation failed: ${itemData.error}`, {
        duration: 4000,
        position: 'bottom-right'
      });
    } else {
      // Show success notification for normal updates
      toast.success(`Item "${itemData.title}" metadata updated`, {
        duration: 2000,
        position: 'bottom-right'
      });
    }
  }, [fetchCollections]);

  const handleCollectionUpdated = useCallback((collectionId, collectionData) => {
    console.log('[Knowledge] Collection updated via WebSocket:', collectionId, collectionData);

    // Update collections list in sidebar
    setCollections(prev => {
      const updated = prev.map(col =>
        col.id === collectionId
          ? { ...col, title: collectionData.title, category: collectionData.category, tags: collectionData.tags }
          : col
      );
      return updated;
    });

    // Update loadedCollections if this collection is currently loaded
    setLoadedCollections(prev => {
      if (prev[collectionId]) {
        return {
          ...prev,
          [collectionId]: {
            ...prev[collectionId],
            title: collectionData.title,
            category: collectionData.category,
            tags: collectionData.tags
          }
        };
      }
      return prev;
    });

    // Show toast notification
    toast.success(`Collection "${collectionData.title}" metadata updated`, {
      duration: 2000,
      position: 'bottom-right'
    });
  }, []);

  const handleImportProgress = useCallback((progressData) => {
    console.log('[Knowledge] Document import progress update via WebSocket:', progressData);

    // Get current progress state to preserve fileName
    const currentProgress = getKnowledgeImportProgress();

    // Update progress state with WebSocket data
    setKnowledgeImportProgress({
      fileName: currentProgress?.fileName || null,
      status: progressData.status || 'generating',
      progress: progressData.progress || 75,
      error: null,
      result: null,
    });
  }, [getKnowledgeImportProgress, setKnowledgeImportProgress]);

  const handleImportCompleted = useCallback((importData) => {
    console.log('[Knowledge] Document import completed via WebSocket:', importData);

    if (importData.success) {
      // Update progress state to complete
      setKnowledgeImportProgress({
        fileName: null,
        status: 'complete',
        progress: 100,
        error: null,
        result: {
          collections_created: importData.collections_count || 0,
          items_created: importData.items_count || 0,
        },
      });

      // Show success notification
      toast.success(
        `Document import complete: ${importData.collections_count} collections, ${importData.items_count} items`,
        {
          duration: 4000,
          position: 'bottom-right'
        }
      );

      // Refresh collections list to show updated metadata
      fetchCollections();
    } else {
      // Update progress state to error
      setKnowledgeImportProgress({
        fileName: null,
        status: 'error',
        progress: 0,
        error: importData.error || 'Unknown error',
        result: null,
      });

      // Show error notification
      toast.error(`Document import failed: ${importData.error || 'Unknown error'}`, {
        duration: 5000,
        position: 'bottom-right'
      });
    }
  }, [setKnowledgeImportProgress, getKnowledgeImportProgress, fetchCollections]);

  // Initialize WebSocket connection for real-time updates
  const { isConnected, error } = useKnowledgeWebSocket({
    onItemUpdated: handleItemUpdated,
    onCollectionUpdated: handleCollectionUpdated,
    onImportProgress: handleImportProgress,
    onImportCompleted: handleImportCompleted
  });

  /**
   * Note: This component doesn't use the useKnowledgeData hook because it needs
   * additional data (categories) and uses a different API endpoint for products
   * (knowledgeApi.filters.getProducts() vs knowledgeApi.products.getAll()).
   *
   * The Knowledge page has more complex filtering requirements, so it manages
   * its own data fetching. The useKnowledgeData hook is designed for simpler
   * use cases like the FloatingActionMenu in Home.jsx.
   */

  // Fetch filters function
  const fetchFilters = async () => {
    try {
      const [productsData, categoriesData] = await Promise.all([
        knowledgeApi.filters.getProducts(),
        knowledgeApi.filters.getCategories()
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to fetch filter data:', error);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchFilters();
    fetchCollections();
  }, [fetchCollections]);

  // Re-fetch collections when search term changes
  useEffect(() => {
    fetchCollections(searchTerm);
  }, [searchTerm, fetchCollections]);

  // Load full collection data for restored selected collections on mount
  useEffect(() => {
    const loadRestoredCollections = async () => {
      const validCollectionIds = [];

      for (const collectionId of selectedCollections) {
        // Only load if not already loaded
        if (!loadedCollections[collectionId]) {
          try {
            await refreshCollection(collectionId);
            validCollectionIds.push(collectionId);
          } catch (error) {
            // Collection no longer exists (404) - remove from state
            if (error.status === 404) {
              console.warn(`Removing deleted collection ${collectionId} from persisted state`);
              // Don't add to validCollectionIds - it will be filtered out
            } else {
              console.error(`Failed to fetch restored collection ${collectionId}:`, error);
              // For other errors, keep the ID to try again later
              validCollectionIds.push(collectionId);
            }
          }
        } else {
          validCollectionIds.push(collectionId);
        }
      }

      // Clean up selectedCollections if any were removed
      if (validCollectionIds.length !== selectedCollections.length) {
        setSelectedCollections(validCollectionIds);

        // If locked collection was removed, clear it
        if (lockedCollectionId && !validCollectionIds.includes(lockedCollectionId)) {
          setLockedCollectionId(validCollectionIds.length > 0 ? validCollectionIds[0] : null);
        }
      }
    };

    // Only run on initial mount if there are selected collections from persisted state
    if (selectedCollections.length > 0) {
      loadRestoredCollections();
    }
  }, []); // Empty deps - only run once on mount

  // Persist state changes to UIState
  useEffect(() => {
    setKnowledgeSelectedCollections(selectedCollections);
  }, [selectedCollections, setKnowledgeSelectedCollections]);

  useEffect(() => {
    setKnowledgeLockedCollection(lockedCollectionId);
  }, [lockedCollectionId, setKnowledgeLockedCollection]);

  useEffect(() => {
    setKnowledgeSearchTerm(searchTerm);
  }, [searchTerm, setKnowledgeSearchTerm]);

  useEffect(() => {
    setKnowledgeSelectedProduct(selectedProduct);
  }, [selectedProduct, setKnowledgeSelectedProduct]);

  useEffect(() => {
    setKnowledgeSelectedCategory(selectedCategory);
  }, [selectedCategory, setKnowledgeSelectedCategory]);

  useEffect(() => {
    setKnowledgeContextFilters(contextFilters);
  }, [contextFilters, setKnowledgeContextFilters]);

  useEffect(() => {
    setKnowledgeSharedSlotPositions(sharedSlotPositions);
  }, [sharedSlotPositions, setKnowledgeSharedSlotPositions]);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle filters from AI Response Details navigation
  useEffect(() => {
    if (location.state?.fromAIResponse && location.state?.collectionFilters) {
      const { collectionFilters } = location.state;

      // Add the collection filters to context filters
      setContextFilters(collectionFilters);

      // Clear the navigation state to prevent re-applying filters on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Sidebar configuration - wider sidebar with responsive mode
  // At 415px or below: 64 (256px), otherwise 96 (384px)
  const sidebarWidth = windowWidth <= 415 ? 64 : 96;
  const sidebarMode = windowWidth <= 980 ? 'overlay' : 'push';
  const sidebarWidthPx = sidebarWidth * 4;

  const handleCollectionClick = async (collectionId) => {
    // Handle locking/unlocking logic
    if (collectionId === lockedCollectionId) {
      // Clicking on locked collection: unlock it
      const otherCollections = selectedCollections.filter(id => id !== collectionId);
      if (otherCollections.length > 0) {
        // Auto-lock the first remaining collection
        setLockedCollectionId(otherCollections[0]);
        setSelectedCollections(otherCollections);
      } else {
        // No other collections, just clear everything
        setLockedCollectionId(null);
        setSelectedCollections([]);
      }
      return;
    }

    if (selectedCollections.includes(collectionId)) {
      // Clicking on unlocked selected collection: deselect it
      setSelectedCollections(prev => prev.filter(id => id !== collectionId));
      return;
    }

    // Clicking on unselected collection
    if (lockedCollectionId === null) {
      // No locked collection yet: lock this one
      setLockedCollectionId(collectionId);
      setSelectedCollections([collectionId]);
    } else {
      // Locked collection exists: add/replace as unlocked selection
      const unlockedCollections = selectedCollections.filter(id => id !== lockedCollectionId);
      if (unlockedCollections.length === 0) {
        // No unlocked selection yet: add this one
        setSelectedCollections([lockedCollectionId, collectionId]);
      } else {
        // Replace existing unlocked selection
        setSelectedCollections([lockedCollectionId, collectionId]);
      }
    }

    // Lazy-load full collection data if not already loaded
    if (!loadedCollections[collectionId]) {
      try {
        await refreshCollection(collectionId);
      } catch (error) {
        console.error(`Failed to fetch collection ${collectionId}:`, error);
      }
    }
  };

  // Filter collections based on search term, product, category, and context filters
  const filteredCollections = collections.filter(collection => {
    // Search is now handled by backend, so we only filter by product, category, and AI filters
    const matchesProduct = selectedProduct === 'all' || collection.product_id === selectedProduct;
    const matchesCategory = selectedCategory === 'all' || collection.category === selectedCategory;

    // Apply AI response collection filters
    const aiResponseFilters = contextFilters.filter(f => f.type === 'ai-response-collection');
    const matchesAIFilters = aiResponseFilters.length === 0 ||
      aiResponseFilters.some(f => f.collectionId === collection.id);

    return matchesProduct && matchesCategory && matchesAIFilters;
  });

  // Get full collection data for viewing in graph
  // Prefer loaded collection data (with slots) over summary data
  // Ensure locked collection is first in the array (top position in dual view)
  const viewingCollections = selectedCollections
    .sort((a, b) => {
      if (a === lockedCollectionId) return -1;
      if (b === lockedCollectionId) return 1;
      return 0;
    })
    .map(id => loadedCollections[id] || collections.find(c => c.id === id))
    .filter(Boolean);

  const handlePageClick = () => {
    setActiveCollectionId(null);
  };

  const handleAddFilter = (filter) => {
    const newFilter = {
      ...filter,
      id: Date.now() + Math.random(), // Unique ID
    };
    setContextFilters(prev => [...prev, newFilter]);
  };

  const handleRemoveFilter = (filterId) => {
    setContextFilters(prev => prev.filter(f => f.id !== filterId));
  };

  const handleItemClick = async (item) => {
    // Check if item has full details (content, image, attached_item)
    // If not, fetch the full item data
    let fullItem = item;
    if (!item.content && !item.image && !item.attached_item) {
      try {
        fullItem = await knowledgeApi.items.getById(item.id);
      } catch (error) {
        console.error(`Failed to fetch item ${item.id}:`, error);
        // Fall back to partial data if fetch fails
        fullItem = item;
      }
    }

    // If sidebar is collapsed, show modal instead of sidebar details
    if (isSidebarCollapsed) {
      setModalItem(fullItem);
    } else {
      setSelectedItem(fullItem);
    }
  };

  /**
   * Handle item update.
   * Updates the item and refreshes all collections that contain this item.
   */
  const handleItemUpdate = async (itemId, updates) => {
    try {
      // Update the item
      const updatedItem = await knowledgeApi.items.update(itemId, updates);

      // Update the selected item if it's the same one
      if (selectedItem?.id === itemId) {
        setSelectedItem(updatedItem);
      }
      if (modalItem?.id === itemId) {
        setModalItem(updatedItem);
      }

      // Refresh all collections that contain this item (in parallel)
      await refreshCollectionsWithItem(loadedCollections, itemId);

      // Refresh the collections list to update any metadata changes
      await fetchCollections();

      toast.success('Item updated successfully');
    } catch (error) {
      console.error('Failed to update item:', error);
      toast.error('Failed to update item. Please try again.');
      throw error;
    }
  };

  /**
   * Unlink an item from a collection.
   * Used when right-clicking an item and selecting "Unlink" or "Unlink from Both".
   * Refreshes all currently viewed collections to update shared slots display.
   */
  const handleUnlinkItem = async (itemId, collectionId) => {
    try {
      await knowledgeApi.items.unlink({
        itemId,
        collectionId
      });

      // Refresh all currently viewed collections to update the UI
      // This is important for dual view where shared slots need to update
      const collectionsToRefresh = selectedCollections.includes(collectionId)
        ? selectedCollections
        : [collectionId];

      await refreshCollections(collectionsToRefresh);

      toast.success('Item unlinked successfully');
    } catch (error) {
      console.error('Failed to unlink item:', error);
      toast.error('Failed to unlink item. Please try again.');
    }
  };

  /**
   * Handle collection deletion.
   * Deletes the collection and automatically cleans up orphaned items.
   */
  const handleDeleteCollection = async (collectionId) => {
    try {
      // Delete the collection
      await knowledgeApi.collections.delete(collectionId);

      // Remove from selected collections
      setSelectedCollections(prev => prev.filter(id => id !== collectionId));

      // Clear locked state if this was the locked collection
      if (lockedCollectionId === collectionId) {
        setLockedCollectionId(null);
      }

      // Remove from loaded collections
      setLoadedCollections(prev => {
        const newLoaded = { ...prev };
        delete newLoaded[collectionId];
        return newLoaded;
      });

      // Refresh the full collections list
      await fetchCollections();

      toast.success('Collection deleted successfully');
    } catch (error) {
      console.error('Failed to delete collection:', error);
      toast.error('Failed to delete collection. Please try again.');
    }
  };

  /**
   * Unselect a collection from the graph view
   */
  const handleUnselectCollection = (collectionId) => {
    // Remove from selected collections
    setSelectedCollections(prev => {
      const filtered = prev.filter(id => id !== collectionId);

      // If we're unselecting the locked collection
      if (collectionId === lockedCollectionId) {
        // If there are other collections, lock the first one
        if (filtered.length > 0) {
          setLockedCollectionId(filtered[0]);
        } else {
          // No collections left, clear locked state
          setLockedCollectionId(null);
        }
      }

      return filtered;
    });
  };

  /**
   * Handle empty slot click to open create item modal.
   * Accepts single collection ID or array of collection IDs (for shared slots).
   */
  const handleEmptySlotClick = (collectionIds) => {
    // Ensure collectionIds is an array and save for later use
    const collectionsArray = Array.isArray(collectionIds) ? collectionIds : [collectionIds];
    setPreselectedCollections(collectionsArray);

    // Check if products exist before opening Create Item modal
    if (!products || products.length === 0) {
      // No products exist, open Create Product modal instead
      setProductModalContext('emptySlot');
      setIsCreateProductModalOpen(true);
    } else {
      // Products exist, open Create Item modal with preselected collections
      setIsCreateModalOpen(true);
    }
  };

  /**
   * Handle item created from modal.
   * Refreshes collections to show the newly created item.
   */
  const handleItemCreated = async (item) => {
    // Refresh all currently selected collections (in parallel)
    await refreshCollections(selectedCollections);

    // Refresh the full collections list
    fetchCollections();
  };

  /**
   * Handle product created from CreateProductModal.
   * Refreshes products and reopens the CreateItemModal with preselected collections.
   */
  const handleProductCreated = async () => {
    // Refresh the product filters
    await fetchFilters();
    // Open the CreateItemModal so user can continue creating the item
    // (preselectedCollections will be used if this was triggered from an empty slot click)
    setIsCreateProductModalOpen(false);
    setIsCreateModalOpen(true);
    // Clear the context
    setProductModalContext(null);
  };

  /**
   * Handle item drop operations for drag-and-drop functionality.
   * Supports four scenarios:
   * 1. Shared → Shared: Moving between shared slots (frontend-only, no backend changes)
   * 2. Regular → Shared: Making an item shared between both collections
   * 3. Shared → Regular: Removing an item from shared status
   * 4. Regular → Regular: Standard move/swap between regular slots
   */
  const handleItemDrop = async (dropData) => {
    try {
      const { itemId, sourceCollectionId, sourceSlotPosition, targetCollectionId, targetSlotPosition } = dropData;

      const isSourceShared = sourceCollectionId === 'shared';
      const isTargetShared = targetCollectionId === 'shared';

      let collectionsToRefresh = [];
      const viewingCollectionIds = selectedCollections;

      // ========================================
      // SCENARIO 1: Shared → Shared
      // ========================================
      // Moving an item between shared slots (e.g., shared slot 0 → shared slot 1)
      // This is a frontend-only operation - we just update which shared slot displays the item
      // The item remains linked to both collections at their same positions in the backend
      if (isSourceShared && isTargetShared) {
        if (sourceSlotPosition !== targetSlotPosition) {
          const pairKey = [...viewingCollectionIds].sort().join('_');
          setSharedSlotPositions(prev => {
            const currentPositions = { ...(prev[pairKey] || {}) };
            delete currentPositions[sourceSlotPosition];
            currentPositions[targetSlotPosition] = itemId;
            return {
              ...prev,
              [pairKey]: currentPositions
            };
          });
        }
        return; // No backend changes needed
      }

      // ========================================
      // SCENARIO 2: Regular → Shared
      // ========================================
      // Making an item shared by linking it to both collections
      //
      // Example: Item X at Collection A slot 2 → drag to shared slot
      // Result: Item X moves to A-0, links to B-0, A-2 becomes empty, appears in shared slot
      //
      // Steps:
      // 1. Update frontend memory to track which shared slot this item occupies
      // 2. Find available empty slots in BOTH collections
      // 3. Move item to available slot in source collection (frees up original slot)
      // 4. Link item to available slot in other collection
      if (!isSourceShared && isTargetShared) {
        const pairKey = [...viewingCollectionIds].sort().join('_');

        // Update shared slot position memory (frontend state for rendering)
        setSharedSlotPositions(prev => ({
          ...prev,
          [pairKey]: {
            ...(prev[pairKey] || {}),
            [targetSlotPosition]: itemId
          }
        }));

        const otherCollectionId = viewingCollectionIds.find(id => id !== sourceCollectionId);
        const otherCollection = loadedCollections[otherCollectionId];
        const sourceCollection = loadedCollections[sourceCollectionId];

        // Find first available empty slot in the other collection
        let availableSlotInOther = null;
        if (otherCollection?.slots) {
          for (let i = 0; i < 4; i++) {
            const slot = otherCollection.slots.find(s => s.position === i);
            if (!slot?.item) {
              availableSlotInOther = i;
              break;
            }
          }
        }

        // Find first available empty slot in source collection (excluding the source position)
        let availableSlotInSource = null;
        if (sourceCollection?.slots) {
          for (let i = 0; i < 4; i++) {
            const slot = sourceCollection.slots.find(s => s.position === i);
            if (!slot?.item && i !== sourceSlotPosition) {
              availableSlotInSource = i;
              break;
            }
          }
        }

        // Validate we have available slots in both collections
        if (availableSlotInOther === null || availableSlotInSource === null) {
          toast.error('Cannot move to shared slot: not enough empty slots in both collections');
          return;
        }

        // Move item to new position in source collection (this frees up the original slot)
        await knowledgeApi.items.move({
          itemId,
          sourceCollectionId,
          sourceSlotPosition,
          targetCollectionId: sourceCollectionId,
          targetSlotPosition: availableSlotInSource
        });

        // Link item to the other collection at available position
        await knowledgeApi.items.link({
          itemId,
          collectionId: otherCollectionId,
          slotPosition: availableSlotInOther
        });

        collectionsToRefresh = viewingCollectionIds;
      }

      // ========================================
      // SCENARIO 3: Shared → Regular
      // ========================================
      // Removing an item from shared status by unlinking from one collection
      //
      // Example: Item X shared at A-0 and B-0 → drag from shared slot to regular slot in Collection A
      // Result: Item X unlinks from B, moves to target position in A, no longer shared
      //
      // Steps:
      // 1. Unlink item from the collection it's NOT being dropped into
      // 2. Move item to target position within the target collection
      // 3. Remove from shared slot position memory
      else if (isSourceShared && !isTargetShared) {
        // Unlink from the collection that's NOT receiving the drop
        const collectionToUnlinkFrom = viewingCollectionIds.find(id => id !== targetCollectionId);
        await knowledgeApi.items.unlink({
          itemId,
          collectionId: collectionToUnlinkFrom
        });

        // Find the item's current position in the target collection
        const targetCollection = loadedCollections[targetCollectionId];
        const currentSlot = targetCollection?.slots?.find(s => s.item?.id === itemId);
        const currentPosition = currentSlot?.position ?? 0;

        // If current position differs from target, move item within the collection
        if (currentPosition !== targetSlotPosition) {
          await knowledgeApi.items.move({
            itemId,
            sourceCollectionId: targetCollectionId,
            sourceSlotPosition: currentPosition,
            targetCollectionId,
            targetSlotPosition
          });
        }

        // Remove from shared slot position memory (frontend state cleanup)
        const pairKey = [...viewingCollectionIds].sort().join('_');
        setSharedSlotPositions(prev => {
          const currentPositions = { ...prev[pairKey] };
          delete currentPositions[sourceSlotPosition];
          return {
            ...prev,
            [pairKey]: currentPositions
          };
        });

        // Refresh both collections to get updated slot positions
        // Note: Only collectionToUnlinkFrom will show loading state (metadata regen)
        // but we need to refresh both to get accurate slot data from backend
        collectionsToRefresh = viewingCollectionIds;
      }

      // ========================================
      // SCENARIO 4: Regular → Regular
      // ========================================
      // Standard move or swap between regular slots (same or different collections)
      //
      // Special case: If target slot contains a shared item that's rendered in a shared slot,
      // treat the regular slot as empty to prevent unlinking the shared item
      //
      // Example: Item X shared (in shared slot), Item Y at A-2 → drag Y to B-2 (where X exists)
      // Since X is in a shared slot, B-2 appears empty → Y moves to B-2, X stays shared
      else {
        const targetCollection = loadedCollections[targetCollectionId];
        const targetSlot = targetCollection?.slots?.find(s => s.position === targetSlotPosition);
        const targetItem = targetSlot?.item;

        let isTargetItemShared = false;
        let isTargetItemInSharedSlot = false;

        // Check if target slot contains a shared item (cross-collection scenario only)
        if (targetItem && sourceCollectionId !== targetCollectionId) {
          const sourceCollection = loadedCollections[sourceCollectionId];
          isTargetItemShared = sourceCollection?.slots?.some(s => s.item?.id === targetItem.id);

          // If shared, check if it's currently being displayed in a shared slot
          if (isTargetItemShared) {
            const pairKey = [...viewingCollectionIds].sort().join('_');
            const currentSharedPositions = sharedSlotPositions[pairKey] || {};
            isTargetItemInSharedSlot = Object.values(currentSharedPositions).includes(targetItem.id);
          }
        }

        // Handle edge case: Shared item exists at target but NOT in a shared slot
        // We need to unlink it first to prevent duplication when swapping
        if (isTargetItemShared && !isTargetItemInSharedSlot) {
          await knowledgeApi.items.unlink({
            itemId: targetItem.id,
            collectionId: sourceCollectionId
          });
        }
        // If target item IS in a shared slot, we treat the regular slot as empty (no action needed)

        // Perform the move/swap operation
        const result = await knowledgeApi.items.move(dropData);
        collectionsToRefresh = result.affected_collections || [sourceCollectionId, targetCollectionId];
      }

      // ========================================
      // REFRESH COLLECTIONS
      // ========================================
      // Fetch updated collection data to reflect changes in the UI (in parallel)
      await Promise.all([
        refreshCollections(collectionsToRefresh),
        fetchCollections() // Also refresh sidebar list to show loading state
      ]);
    } catch (error) {
      console.error('Failed to move item:', error);
      toast.error('Failed to move item. Please try again.');
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--color-gradient-from) 0%, var(--color-gradient-via) 50%, var(--color-gradient-to) 100%)' }} onClick={handlePageClick}>
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(var(--color-border-subtle) 1px, transparent 1px), linear-gradient(90deg, var(--color-border-subtle) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        opacity: 0.3
      }}></div>

      {/* Animated background accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--color-accent-primary)', opacity: 0.08 }}></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--color-info)', opacity: 0.08 }}></div>
      </div>

      {/* Sidebar */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        width={sidebarWidth}
        mode={sidebarMode}
        autoCollapseOnOverlay={true}
      >
        <KnowledgeSidebar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          filteredCollections={filteredCollections}
          selectedCollections={selectedCollections}
          lockedCollectionId={lockedCollectionId}
          handleCollectionClick={handleCollectionClick}
          issues={[]}
          contextFilters={contextFilters}
          onRemoveFilter={handleRemoveFilter}
          onAddFilter={handleAddFilter}
          selectedItem={selectedItem}
          onCloseItemDetails={() => setSelectedItem(null)}
          onUpdateItem={handleItemUpdate}
          products={products}
          categories={categories}
          onDeleteCollection={handleDeleteCollection}
          onUnselectCollection={handleUnselectCollection}
          onRefreshCollections={fetchCollections}
          collections={collections}
          onCreateProduct={() => setIsCreateProductModalOpen(true)}
        />
      </Sidebar>

      {/* Main Content - Responsive positioning */}
      <main
        className={`fixed top-0 right-0 bottom-0 overflow-hidden px-4 py-8 transition-all duration-300`}
        style={{
          left: sidebarMode === 'overlay' || isSidebarCollapsed ? '0' : `${sidebarWidthPx}px`
        }}
      >
        {/* Close Button - Top Right */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate('/home');
          }}
          className="absolute top-8 right-8 p-3 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl z-10"
          style={{
            backgroundColor: 'var(--color-bg-elevated)',
            borderWidth: '1px',
            borderColor: 'var(--color-border-default)',
            color: 'var(--color-text-muted)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
            e.currentTarget.style.borderColor = 'var(--color-border-strong)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-elevated)';
            e.currentTarget.style.borderColor = 'var(--color-border-default)';
            e.currentTarget.style.color = 'var(--color-text-muted)';
          }}
          title="Close Knowledge Base"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Graph Area */}
        <div className="w-full h-full flex items-center justify-center">
          <GraphView
            collections={viewingCollections}
            lockedCollectionId={lockedCollectionId}
            activeCollectionId={activeCollectionId}
            setActiveCollectionId={setActiveCollectionId}
            onAddFilter={handleAddFilter}
            onItemClick={handleItemClick}
            onDrop={handleItemDrop}
            onUnlink={handleUnlinkItem}
            onEmptySlotClick={handleEmptySlotClick}
            onDeleteCollection={handleDeleteCollection}
            onUnselectCollection={handleUnselectCollection}
            sharedSlotPositions={sharedSlotPositions}
            setSharedSlotPositions={setSharedSlotPositions}
          />
        </div>
      </main>

      {/* Floating Action Menu - Outside main to avoid z-index stacking issues */}
      <FloatingActionMenu
        collections={collections}
        products={products}
        onRefreshProducts={fetchFilters}
        onRefreshCollections={fetchCollections}
      />

      {/* Create Item Modal */}
      <CreateItemModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        collections={collections}
        onItemCreated={handleItemCreated}
        preselectedCollections={preselectedCollections}
      />

      {/* Create Product Modal */}
      <CreateProductModal
        isOpen={isCreateProductModalOpen}
        onClose={() => {
          setIsCreateProductModalOpen(false);
          setProductModalContext(null);
        }}
        onProductCreated={handleProductCreated}
        triggeredFrom={productModalContext}
      />

      {/* Item Details Modal (shown when sidebar is collapsed) */}
      <ItemDetailsModal
        item={modalItem}
        isOpen={modalItem !== null}
        onClose={() => setModalItem(null)}
        onUpdate={handleItemUpdate}
      />

      {/* WebSocket Connection Status Indicator */}
      {!isConnected && (
        <div className={`fixed bottom-4 right-4 px-4 py-2 ${error ? 'bg-red-900/90 border-red-700' : 'bg-yellow-900/90 border-yellow-700'} border rounded-lg shadow-lg z-50 flex items-center gap-2`}>
          <div className={`w-2 h-2 rounded-full ${error ? 'bg-red-500' : 'bg-yellow-500'} animate-pulse`} />
          <span className={`text-sm ${error ? 'text-red-100' : 'text-yellow-100'}`}>
            {error || 'Connecting...'}
          </span>
        </div>
      )}
    </div>
  );
}
